import { useMutation, useQuery } from '@apollo/client/react'
import {
  useTranslate,
  type DocumentStatus,
  type ExtractionState,
  type GenerationState,
  type ImageStatus,
  type SelectedDocument,
  type SelectedImage,
} from '@athena/ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { PLANS, type QuestionType, type PracticeGenerationProvider } from '@athena/shared'

import {
  ExtractPagesDocument,
  GeneratePracticeDocument,
  SubscriptionUsageDocument,
} from '../../graphql/generated/operations.js'
import { useUserErrorText } from '../../lib/user-errors.js'

import {
  COMPRESSION_FAILED,
  extractPages,
  failedCount,
  initialPages,
  type PageState,
} from './extraction/extractPages.js'
import { MAX_IMAGE_MEGABYTES, MAX_IMAGES, type RejectedFile } from './imageSelection.js'
import { MAX_PDF_MEGABYTES, MAX_PDFS, toBase64, type RejectedPdf } from './pdfSelection.js'
import { DEFAULT_PRACTICE_SIZE, questionsForSize, type PracticeSize } from './practiceSize.js'
import { useImageSelection, type ChosenImage } from './useImageSelection.js'
import { usePdfSelection, type ChosenPdf } from './usePdfSelection.js'

/**
 * The two kinds of source material, and never both at once.
 *
 * Images go to a model, which reads the pixels. PDFs go to a parser, which
 * reads text that is already in the file. A run is one or the other: the
 * server refuses a request carrying both, and this is the browser's half of
 * keeping a tutor out of that state.
 */
export type SourceMode = 'image' | 'pdf'

export interface AutomaticPractice {
  subjectId: string
  setSubjectId: (subjectId: string) => void
  /**
   * How long the practice should be, as the tutor chose it.
   *
   * Resolved to a question count on the way into the mutation and nowhere
   * else — see `practiceSize.ts` for why the sizes stop at the network.
   */
  size: PracticeSize
  setSize: (size: PracticeSize) => void
  questionTypes: QuestionType[]
  setQuestionTypes: (value: QuestionType[]) => void
  allowedQuestionTypes: readonly QuestionType[]
  providerOverrideEnabled: boolean
  localDevelopmentEnabled: boolean
  provider: PracticeGenerationProvider
  setProvider: (value: PracticeGenerationProvider) => void
  /**
   * Which kind of source material this run will use.
   *
   * Derived from the selection whenever there is one, and a preference only
   * while there is not: adding the first image puts the tab in image mode and
   * adding the first PDF puts it in PDF mode. There is no third state and no
   * way to reach one — the two selections cannot both be non-empty, because
   * the uploader for the other kind is not on screen to add to.
   */
  sourceMode: SourceMode
  /**
   * Choose which uploader to show. Only meaningful before anything is chosen;
   * once it is, `sourceMode` follows the selection and this does nothing.
   */
  setSourceMode: (mode: SourceMode) => void
  /**
   * True once something is selected, when the mode has stopped being a choice.
   *
   * What the form reads to swap the chooser for a line explaining how to
   * change kind — which is to remove what is there.
   */
  sourceLocked: boolean
  /** The selection, each image carrying where its reading has got to. */
  images: readonly SelectedImage[]
  /** The chosen PDFs, each carrying where its reading has got to. */
  documents: readonly SelectedDocument[]
  /**
   * TEMPORARY diagnostic: what each image weighed before and after the
   * browser compressed it.
   *
   * Here so a tutor — and whoever is tuning `compressImage.ts` — can see what
   * the upload actually costs, rather than inferring it. Delete this field,
   * `ImageSizeReport` and its two dictionary keys together when the numbers
   * have been seen enough times.
   */
  sizes: readonly ImageSize[]
  /** Already-localized sentences about the files the last choice turned away. */
  rejections: readonly string[]
  extraction: ExtractionState
  generation: GenerationState
  /** What to say out loud about the last thing that happened. */
  announcement: string
  /** True while a reading is running: the selection is locked for the duration. */
  reading: boolean
  /**
   * True from the first click until the whole run is over, reading included.
   *
   * What the Generate button and the uploaders are disabled by. Genuinely one
   * piece of state spanning both stages rather than the two stage flags
   * combined: those are cleared and set in separate microtasks, so a derived
   * flag went false for a frame in the middle of a run and let a second one
   * start over the same pages.
   */
  running: boolean
  /** Whether anything is still missing before a reading can start. */
  incomplete: boolean
  addImages: (files: readonly File[]) => void
  removeImage: (id: string) => void
  clearImages: () => void
  addDocuments: (files: readonly File[]) => void
  removeDocument: (id: string) => void
  clearDocuments: () => void
  /**
   * The whole flow: read every image that is not already done, then generate.
   * What the Generate button calls.
   */
  read: () => Promise<void>
  /**
   * Generate again from the pages already read.
   *
   * The retry path, and the reason the pages are kept: it makes a new request
   * to the provider and does not touch an image. Which provider that is was
   * never this file's to choose.
   */
  regenerate: () => Promise<void>
  /**
   * The document the next step will turn into a practice, or null before a
   * reading has produced one.
   *
   * Whole or absent, never partial: the server refuses a transcription that
   * does not cover every page, so there is no state in which this holds four
   * pages of five.
   */
  combinedMarkdown: string | null
  /** The persisted draft to open after generation succeeds. */
  createdPracticeId: string | null
}

/**
 * Everything the automatic tab knows.
 *
 * It lives above the tab so that looking at the Manual tab and coming back
 * does not throw away a tutor's images and the pages already read. The manual
 * tab's document survives a tab switch because the screen has always held it;
 * this is the same guarantee for the other tab.
 *
 * ── The shape of a run ───────────────────────────────────────────────────
 *
 * Compress every image in the browser, in selection order, then send all of
 * them to Athena's API in one mutation and get one document back. One round
 * trip rather than one per page, and the reader sees the pages as the single
 * document they are.
 *
 * A failure is therefore the whole reading rather than one tile: there is no
 * per-page retry, and the way out is to read the pages again or to remove the
 * photograph that came out badly. The guarantee that survives is the one that
 * matters — the server refuses a transcription that does not cover every page,
 * so a document here is never missing one.
 *
 * Nothing here talks to the extraction provider. The browser sends images to
 * Athena's own API and gets Markdown back — the credentials are the server's,
 * and this file could not reach them if it tried.
 */
/** TEMPORARY diagnostic: one image's weight, before and after compression. */
export interface ImageSize {
  id: string
  name: string
  /** The file the tutor chose, as it sits on their disk. */
  originalBytes: number
  /** What will actually be sent. Null until the browser has prepared it. */
  compressedBytes: number | null
}

export interface PracticeGenerationCapability {
  localDevelopmentEnabled?: boolean
  providerOverrideEnabled: boolean
  configuredProvider?: PracticeGenerationProvider | null
}

export function useAutomaticPractice(capability?: PracticeGenerationCapability): AutomaticPractice {
  const t = useTranslate()
  const userErrorText = useUserErrorText()
  const { images, rejected, add, remove, clear } = useImageSelection()
  const pdfs = usePdfSelection()
  /**
   * Which uploader to show while nothing has been chosen.
   *
   * A preference rather than the mode: the moment a file exists the selection
   * decides, and this is only what the tab opens on. Images, because a
   * photograph of a worksheet is the commoner case.
   */
  const [preferredMode, setPreferredMode] = useState<SourceMode>('image')
  /** Per-document reading state, keyed by document id. */
  const [documentState, setDocumentState] = useState<
    ReadonlyMap<string, { status: DocumentStatus; errorMessage?: string }>
  >(new Map())

  const [subjectId, setSubjectId] = useState('')
  const [size, setSize] = useState<PracticeSize>(DEFAULT_PRACTICE_SIZE)
  const [questionTypes, setQuestionTypes] = useState<QuestionType[]>(['MULTIPLE_CHOICE'])
  const subscription = useQuery(SubscriptionUsageDocument)
  const planCode = subscription.data?.mySubscription.planCode ?? 'FREE'
  const allowedQuestionTypes = PLANS[planCode].generationQuestionTypes
  useEffect(() => {
    setQuestionTypes((previous) => {
      const permitted = previous.filter((type) => allowedQuestionTypes.includes(type))
      if (permitted.length === previous.length) return previous
      return permitted.length ? permitted : ['MULTIPLE_CHOICE']
    })
  }, [allowedQuestionTypes])
  const [providerOverride, setProvider] = useState<PracticeGenerationProvider | null>(null)
  const providerOverrideEnabled = capability?.providerOverrideEnabled ?? false
  const localDevelopmentEnabled = capability?.localDevelopmentEnabled ?? false
  const provider = providerOverride ?? capability?.configuredProvider ?? 'GEMINI'
  const [pages, setPages] = useState<readonly PageState[]>([])
  /** The document the last reading produced. Cleared whenever the images change. */
  const [readMarkdown, setReadMarkdown] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  /**
   * True from the first click until the whole chain is over.
   *
   * Its own state rather than `reading || generating`, which is what it used
   * to be — and that was a gap rather than a flag. `runOver` clears `reading`
   * in a `finally` and `generateFrom` sets `generating` a microtask later, so
   * React committed a render between the two in which both were false: the
   * Generate button and the file picker went live for a frame in the middle
   * of a run. On a loaded machine the frame is long enough to click in, which
   * is precisely the second run over the same pages this flag exists to stop.
   */
  const [running, setRunning] = useState(false)
  const [generation, setGeneration] = useState<GenerationState>({ status: 'idle' })
  /** The canonical destination returned after generation persists the draft. */
  const [createdPracticeId, setCreatedPracticeId] = useState<string | null>(null)
  const generationKey = useRef<string | null>(null)
  const extractionId = useRef<string | null>(null)
  const [combining, setCombining] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const [extractPagesMutation] = useMutation(ExtractPagesDocument)
  const [generatePractice] = useMutation(GeneratePracticeDocument)

  /** Already-localized, for the sentence under a failed tile. */
  const errorTextFor = useCallback(
    (code: string): string =>
      code === COMPRESSION_FAILED
        ? t('practice.automatic.rejected.unreadableUnnamed')
        : userErrorText({ code, message: '', params: [] }),
    [t, userErrorText],
  )

  const rejectionText = useCallback(
    (rejection: RejectedFile): string => {
      switch (rejection.reason) {
        case 'type':
          return t('practice.automatic.rejected.type', [rejection.name])
        case 'size':
          return t('practice.automatic.rejected.size', [rejection.name, MAX_IMAGE_MEGABYTES])
        case 'count':
          return t('practice.automatic.rejected.count', [rejection.name, MAX_IMAGES])
        case 'unreadable':
          return t('practice.automatic.rejected.unreadable', [rejection.name])
        case 'duplicate':
          return t('practice.automatic.rejected.duplicate', [rejection.name])
      }
    },
    [t],
  )

  /** The same, for a PDF the browser turned away before sending it. */
  const pdfRejectionText = useCallback(
    (rejection: RejectedPdf): string => {
      switch (rejection.reason) {
        case 'type':
          return t('practice.automatic.rejectedPdf.type', [rejection.name])
        case 'size':
          return t('practice.automatic.rejectedPdf.size', [rejection.name, MAX_PDF_MEGABYTES])
        case 'empty':
          return t('practice.automatic.rejectedPdf.empty', [rejection.name])
        case 'count':
          return t('practice.automatic.rejectedPdf.count', [rejection.name, MAX_PDFS])
        case 'batch':
          return t('practice.automatic.rejectedPdf.batch', [rejection.name])
      }
    },
    [t],
  )

  /** Every page to the API and back. The only place the images cross the network. */
  const send = useCallback(
    async (images: readonly { base64: string; mimeType: 'image/jpeg' }[]) => {
      try {
        const result = await extractPagesMutation({
          variables: {
            images: images.map((image) => ({ data: image.base64, mimeType: 'JPEG' as const })),
          },
        })
        const payload = result.data?.extractPages
        extractionId.current = payload?.extractionId ?? null
        if (payload?.markdown) return { ok: true as const, markdown: payload.markdown }

        const code = payload?.userErrors[0]?.code ?? 'EXTRACTION_FAILED'
        // The server answered and said no. Its own log carries the why; this
        // records what the browser was told, so the two can be lined up.
        console.warn(`[athena] extraction refused by the API: ${code}`)
        return { ok: false as const, code }
      } catch (error) {
        /**
         * The request never came back — a rejected body, a dropped
         * connection, CORS. Deliberately its own code rather than sharing
         * `EXTRACTION_FAILED` with a refusal the server sent: the two look
         * identical to a tutor and are nothing alike to fix, and collapsing
         * them is what made a 413 read as "the reader could not be reached".
         *
         * Logged with the real error, because nothing else in the browser
         * will say what it was.
         */
        console.error('[athena] extraction request did not complete', error)
        return { ok: false as const, code: 'EXTRACTION_UNREACHABLE' }
      }
    },
    [extractPagesMutation],
  )

  /**
   * Every chosen PDF to the API and back, and what came of each one.
   *
   * One request for the whole set, like the images — but for a different
   * reason. The images go together because the model reads them as one
   * document; the PDFs go together only because it is one round trip. They are
   * read independently on the server, which is why this can come back with a
   * document *and* a list of files that failed.
   *
   * Nothing here opens a PDF. The bytes are base64'd and sent; the parser, and
   * the decision about what counts as readable text, are the server's.
   */
  const sendPdfs = useCallback(
    async (documents: readonly ChosenPdf[]) => {
      extractionId.current = null
      try {
        const encoded = await Promise.all(
          documents.map(async (document) => ({
            name: document.name,
            data: await toBase64(document.file),
          })),
        )

        const result = await extractPagesMutation({ variables: { pdfs: encoded } })
        const payload = result.data?.extractPages
        const errors = payload?.userErrors ?? []

        if (payload?.markdown) {
          // Both at once is the normal case for PDFs: the documents that were
          // read, and one error per document that was not.
          return { ok: true as const, markdown: payload.markdown, errors }
        }

        const code = errors[0]?.code ?? 'EXTRACTION_FAILED'
        console.warn(`[athena] PDF reading refused by the API: ${code}`)
        return { ok: false as const, errors, code }
      } catch (error) {
        // The request never came back. Same distinction as the image path: a
        // refusal the server sent and a request that never arrived look
        // identical to a tutor and are nothing alike to fix.
        console.error('[athena] PDF reading request did not complete', error)
        return { ok: false as const, errors: [], code: 'EXTRACTION_UNREACHABLE' }
      }
    },
    [extractPagesMutation],
  )

  /**
   * Read every chosen PDF, and hand back the source material.
   *
   * Returns the document rather than only setting state, for the same reason
   * the image path does: the caller goes straight on to generate from it, and
   * reading it back out of `useState` in the same tick would read the previous
   * value.
   */
  const runOverPdfs = useCallback(
    async (documents: readonly ChosenPdf[]): Promise<string | null> => {
      setReading(true)
      setCombining(false)
      setReadMarkdown(null)
      setAnnouncement(t('practice.automatic.readingPdfsAnnouncement'))
      // No client-side preparation stage: a PDF is sent as it is, so every
      // document is "reading" from the moment the request goes out.
      setDocumentState(
        new Map(documents.map((document) => [document.id, { status: 'reading' as const }])),
      )

      try {
        const outcome = await sendPdfs(documents)

        /**
         * Which document each error is about, by the file name the server was
         * given back.
         *
         * Two files with the same name are marked together. That is right when
         * both failed and slightly generous when only one did — and the
         * alternative, an opaque handle the tutor cannot see, would mean an
         * error message that could not name the file it is about.
         */
        const failedNames = new Map(
          outcome.errors
            .filter((error) => error.params.length > 0)
            .map((error) => [error.params[0] as string, userErrorText(error)]),
        )

        setDocumentState(
          new Map(
            documents.map((document) => {
              // A document the server did not name is one it read — when the
              // request as a whole went through. When it did not (it never got
              // an answer, or was refused without naming a file), nothing was
              // read, and each row says so rather than showing a tick.
              const message =
                failedNames.get(document.name) ??
                (outcome.ok
                  ? undefined
                  : userErrorText({ code: 'PDF_FAILED', message: '', params: [document.name] }))
              return [
                document.id,
                message === undefined
                  ? { status: 'read' as const }
                  : { status: 'failed' as const, errorMessage: message },
              ]
            }),
          ),
        )

        if (outcome.ok) {
          setCombining(true)
          setReadMarkdown(outcome.markdown)
          setAnnouncement(
            outcome.errors.length === 0
              ? t('practice.automatic.readPdfsAnnouncement')
              : t('practice.automatic.readSomePdfsAnnouncement', [outcome.errors.length]),
          )
          return outcome.markdown
        }

        // Nothing usable came back. Every failure is already on its own row;
        // this is the sentence for a screen reader.
        setAnnouncement(t('practice.automatic.pdfsFailedAnnouncement'))
        return null
      } finally {
        setReading(false)
      }
    },
    [sendPdfs, t, userErrorText],
  )

  /**
   * Read the pages, and hand back what came of it.
   *
   * Returns the document rather than only setting state, because the caller
   * goes straight on to generate from it and reading it back out of `useState`
   * in the same tick would read the previous value.
   */
  const runOver = useCallback(
    async (from: readonly PageState[]): Promise<string | null> => {
      setReading(true)
      setCombining(false)
      setReadMarkdown(null)
      setAnnouncement(t('practice.automatic.readingAnnouncement'))

      const files = images.map((image) => ({ id: image.id, file: image.file }))

      try {
        const result = await extractPages(files, from, { send, onProgress: setPages })
        setPages(result.pages)

        if (result.ok) {
          setCombining(true)
          setReadMarkdown(result.markdown)
          setAnnouncement(t('practice.automatic.readAnnouncement'))
          return result.markdown
        }

        setAnnouncement(t('practice.automatic.failedAnnouncement'))
        return null
      } finally {
        setReading(false)
      }
    },
    [images, send, t],
  )

  /**
   * The generation half: one document out, one document back.
   *
   * The browser never talks to a model. It calls Athena's own API, which
   * normally decides which provider answers. When the API publishes its
   * development capability, this mutation may carry the selected provider;
   * the server still authorizes and resolves it.
   *
   * Three endings, and the middle one is the interesting one. A practice that
   * came back but does not parse is still handed to the screen, with its
   * errors, because the editor shows both and fixing line 12 beats throwing
   * away a generation that was paid for.
   */
  const generateFrom = useCallback(
    async (sourceMarkdown: string) => {
      if (questionTypes.length === 0) return
      setGeneration({ status: 'running' })
      setAnnouncement(t('practice.automatic.generatingAnnouncement'))

      try {
        generationKey.current ??= crypto.randomUUID()
        const result = await generatePractice({
          variables: {
            subjectId,
            sourceMarkdown,
            // The size becomes a count here, which is the only place it does.
            // `GeneratePracticeInput` takes a number and always did; S/M/L/XL
            // is this client's wording for it.
            questionCount: questionsForSize(size),
            questionTypes,
            ...(providerOverrideEnabled ? { provider } : {}),
            idempotencyKey: generationKey.current,
            ...(extractionId.current ? { extractionId: extractionId.current } : {}),
          },
        })

        const payload = result.data?.generatePractice
        const errors = (payload?.userErrors ?? []).map((error) => ({
          code: error.code,
          message: userErrorText(error),
          line: error.line,
        }))

        if (!payload?.practice) {
          // The first request with this key is still with the model. Keeping
          // the key is what makes pressing again return that request's
          // practice, and spend its one credit, instead of starting another.
          if (errors[0]?.code !== 'GENERATION_IN_PROGRESS') generationKey.current = null
          setGeneration({
            status: 'failed',
            message: errors[0]?.message ?? t('practice.automatic.readFailed'),
          })
          setAnnouncement(t('practice.automatic.generationFailedAnnouncement'))
          return
        }

        setCreatedPracticeId(payload.practice.id)
        setGeneration({ status: 'done' })
        setAnnouncement(t('practice.automatic.generatedAnnouncement'))
      } catch {
        // A transport failure rather than a refusal. Retryable, and said so.
        setGeneration({
          status: 'failed',
          message: userErrorText({ code: 'GENERATION_FAILED', message: '', params: [] }),
        })
        setAnnouncement(t('practice.automatic.generationFailedAnnouncement'))
      }
    },
    [
      generatePractice,
      questionTypes,
      provider,
      providerOverrideEnabled,
      size,
      subjectId,
      t,
      userErrorText,
    ],
  )

  const read = useCallback(async () => {
    // Held across both stages, so the gap between them is not a moment the
    // form is clickable in. Released in the `finally` below, once for the
    // whole chain rather than once per stage.
    setRunning(true)
    // A fresh run of everything, so nothing carries over from a previous one
    // — the previous practice included, edits and all. It described other
    // source material.
    setGeneration({ status: 'idle' })
    setCreatedPracticeId(null)
    generationKey.current = null

    try {
      /**
       * The one branch in the whole flow, and it is here rather than anywhere
       * downstream. Both readers return one Markdown string, so `generateFrom`
       * below cannot tell which one produced it — and neither can the mutation
       * it calls, the provider behind that, or the practice that comes out.
       */
      const combined =
        pdfs.documents.length > 0
          ? await runOverPdfs(pdfs.documents)
          : await runOver(initialPages(images.map((image) => image.id)))

      // Only when there is source material. Generating from nothing, or from
      // four pages of five, would produce a practice missing questions nobody
      // was told about.
      if (combined !== null) await generateFrom(combined)
    } finally {
      setRunning(false)
    }
  }, [generateFrom, images, pdfs.documents, runOver, runOverPdfs])

  /**
   * Generate again from the pages already read.
   *
   * No image is touched: the pages are still in state, so a retry is one
   * request to the provider rather than a second compression and a second
   * transcription of every photograph. It uses the current provider selection
   * under the same server-side capability check as the first attempt.
   */
  const regenerate = useCallback(async () => {
    if (readMarkdown === null) return
    setRunning(true)
    try {
      await generateFrom(readMarkdown)
    } finally {
      setRunning(false)
    }
  }, [generateFrom, readMarkdown])

  /**
   * A changed selection discards the reading: it described other images. And
   * the generation with it, for the same reason — a practice written from
   * pages that are no longer selected is a practice about something else.
   */
  const forget = useCallback(() => {
    setPages([])
    setDocumentState(new Map())
    setReadMarkdown(null)
    setCombining(false)
    setGeneration({ status: 'idle' })
    setCreatedPracticeId(null)
    generationKey.current = null
    setAnnouncement('')
  }, [])

  const addImages = useCallback(
    (files: readonly File[]) => {
      forget()
      void add(files)
    },
    [add, forget],
  )

  const removeImage = useCallback(
    (id: string) => {
      forget()
      remove(id)
    },
    [forget, remove],
  )

  const clearImages = useCallback(() => {
    forget()
    clear()
  }, [clear, forget])

  const addDocuments = useCallback(
    (files: readonly File[]) => {
      forget()
      void pdfs.add(files)
    },
    [forget, pdfs],
  )

  const removeDocument = useCallback(
    (id: string) => {
      forget()
      pdfs.remove(id)
    },
    [forget, pdfs],
  )

  const clearDocuments = useCallback(() => {
    forget()
    pdfs.clear()
  }, [forget, pdfs])

  const byId = new Map(pages.map((page) => [page.id, page]))
  const decorated: SelectedImage[] = images.map((image: ChosenImage) => {
    const page = byId.get(image.id)
    if (!page) return { id: image.id, name: image.name, previewUrl: image.previewUrl }
    return {
      id: image.id,
      name: image.name,
      previewUrl: image.previewUrl,
      status: page.status as ImageStatus,
      ...(page.errorCode ? { errorMessage: errorTextFor(page.errorCode) } : {}),
    }
  })

  /**
   * TEMPORARY diagnostic, joined by id so a size cannot land on the wrong row.
   * Goes when `ImageSizeReport` does.
   */
  const sizes: ImageSize[] = images.map((image: ChosenImage) => ({
    id: image.id,
    name: image.name,
    originalBytes: image.file.size,
    compressedBytes: byId.get(image.id)?.compressedBytes ?? null,
  }))

  const documents: SelectedDocument[] = pdfs.documents.map((document: ChosenPdf) => {
    const state = documentState.get(document.id)
    return {
      id: document.id,
      name: document.name,
      bytes: document.bytes,
      ...(state ? { status: state.status } : {}),
      ...(state?.errorMessage ? { errorMessage: state.errorMessage } : {}),
    }
  })

  /**
   * The mode, derived wherever there is anything to derive it from.
   *
   * The invariant lives here rather than in a piece of state that could
   * disagree with the selection: with an image chosen this is `image` and the
   * PDF uploader is not rendered, so there is no route to a mixed selection.
   * The preference is consulted only when both selections are empty.
   */
  const sourceLocked = images.length > 0 || pdfs.documents.length > 0
  const sourceMode: SourceMode =
    images.length > 0 ? 'image' : pdfs.documents.length > 0 ? 'pdf' : preferredMode

  const combinedMarkdown = readMarkdown
  // The page being compressed, or -1 once every page is out at the reader.
  const preparing = pages.findIndex((page) => page.status === 'compressing')

  /**
   * The image path's own progress, and only the image path's.
   *
   * `ExtractionStatus` counts pages and talks about photographs, which is
   * right for images and wrong for documents — a PDF has no client-side
   * preparation stage to count through and no "clearer photos usually read
   * better" to offer. So in PDF mode this stays idle and the molecule renders
   * nothing; each document carries its own state on its own row instead,
   * which is where a tutor with five files wants it anyway.
   */
  const extraction: ExtractionState =
    sourceMode === 'pdf'
      ? { status: 'idle' }
      : reading
        ? {
            status: 'running',
            // Preparing is per image and countable; reading is one request for all
            // of them and is not.
            stage: preparing === -1 ? 'reading' : 'preparing',
            imageIndex: preparing === -1 ? 0 : preparing,
            imageCount: pages.length,
          }
        : combining && combinedMarkdown !== null
          ? { status: 'done', imageCount: pages.length, markdown: combinedMarkdown }
          : pages.length > 0 && failedCount(pages) > 0
            ? { status: 'incomplete', failedCount: failedCount(pages) }
            : { status: 'idle' }

  return {
    subjectId,
    setSubjectId,
    size,
    setSize,
    questionTypes,
    setQuestionTypes,
    allowedQuestionTypes,
    providerOverrideEnabled,
    localDevelopmentEnabled,
    provider,
    setProvider,
    sourceMode,
    setSourceMode: setPreferredMode,
    sourceLocked,
    images: decorated,
    documents,
    sizes,
    // Whichever uploader is on screen is the one whose refusals are shown.
    rejections:
      sourceMode === 'pdf' ? pdfs.rejected.map(pdfRejectionText) : rejected.map(rejectionText),
    extraction,
    generation,
    announcement,
    reading,
    running,
    // Either kind of source will do; one of them is required. The subject is
    // required whichever it is, because it is what the practice is told it is
    // about.
    incomplete:
      questionTypes.length === 0 ||
      subjectId === '' ||
      (images.length === 0 && pdfs.documents.length === 0),
    addImages,
    removeImage,
    clearImages,
    addDocuments,
    removeDocument,
    clearDocuments,
    read,
    regenerate,
    combinedMarkdown,
    createdPracticeId,
  }
}
